/**
 * Acquisition Channels Page (获客渠道)
 * Per-channel new users / conversion plus affiliate commission totals.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { ButtonGroup } from '@/components/ui/ButtonGroup';
import { Checkbox } from '@/components/ui';
import { Loading } from '@/components/common/Loading';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useI18n } from '@/i18n';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';
import {
  userGrowthApi,
  type AcquisitionChannel,
  type AcquisitionChannelRow,
} from '@/lib/api/userGrowth';

const RANGE_PRESETS = [7, 30, 90, 180] as const;
type RangePreset = (typeof RANGE_PRESETS)[number];

function formatMoney(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

export function AcquisitionChannelsPage() {
  const { messages, t } = useI18n();
  const tt = messages.acquisitionChannelsPage;

  const [days, setDays] = useState<RangePreset>(30);
  const [excludeTeam, setExcludeTeam] = useState(DEFAULT_EXCLUDE_TEAM);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['acquisition-channels', days, excludeTeam],
    queryFn: () => userGrowthApi.getAcquisitionChannels({ days, exclude_team: excludeTeam }),
  });

  const channelLabel = useMemo(
    (): Record<AcquisitionChannel, string> => ({
      referral: t('acquisitionChannelsPage.channelReferral'),
      affiliate: t('acquisitionChannelsPage.channelAffiliate'),
      promo: t('acquisitionChannelsPage.channelPromo'),
      organic: t('acquisitionChannelsPage.channelOrganic'),
    }),
    [t],
  );

  const chartData = useMemo(
    () =>
      (data?.channels ?? []).map((c: AcquisitionChannelRow) => ({
        channel: channelLabel[c.channel] ?? c.channel,
        new_users: c.new_users,
        converted_users: c.converted_users,
      })),
    [data, channelLabel],
  );

  const rangeOptions = useMemo(
    () =>
      RANGE_PRESETS.map((d) => ({
        value: String(d),
        label:
          d === 7
            ? tt?.rangeDays7 ?? '7d'
            : d === 30
              ? tt?.rangeDays30 ?? '30d'
              : d === 90
                ? tt?.rangeDays90 ?? '90d'
                : tt?.rangeDays180 ?? '180d',
      })),
    [tt],
  );

  return (
    <div className="p-6 space-y-6 max-w-[1200px]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('acquisitionChannelsPage.pageTitle')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('acquisitionChannelsPage.pageDescription')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <Checkbox
            label={t('acquisitionChannelsPage.excludeTeam')}
            checked={excludeTeam}
            onChange={(event) => setExcludeTeam(event.target.checked)}
          />
          <ButtonGroup
            options={rangeOptions}
            value={String(days)}
            onChange={(v) => setDays(Number(v) as RangePreset)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="h-72 flex items-center justify-center">
          <Loading message={t('acquisitionChannelsPage.loading')} />
        </div>
      ) : isError ? (
        <ErrorMessage error={t('acquisitionChannelsPage.error')} onRetry={() => refetch()} />
      ) : (
        <>
          {/* Commission totals */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-warning/10">
                  <Wallet className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t('acquisitionChannelsPage.commissionPending')}
                  </p>
                  <p className="text-xl font-semibold text-foreground">
                    {formatMoney(data?.affiliate_commission_pending_cents ?? 0)}
                  </p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-success/10">
                  <Wallet className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t('acquisitionChannelsPage.commissionCleared')}
                  </p>
                  <p className="text-xl font-semibold text-foreground">
                    {formatMoney(data?.affiliate_commission_cleared_cents ?? 0)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Bar chart */}
          <Card>
            <h3 className="text-sm font-medium text-foreground mb-4">
              {t('acquisitionChannelsPage.channelsTitle')}
            </h3>
            {!chartData.length ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                {t('acquisitionChannelsPage.empty')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="channel" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip cursor={{ fillOpacity: 0.1 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="new_users"
                    name={t('acquisitionChannelsPage.colNewUsers')}
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="converted_users"
                    name={t('acquisitionChannelsPage.colConvertedUsers')}
                    fill="#22c55e"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Table */}
          <Card padding={false}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="px-4 py-2">{t('acquisitionChannelsPage.colChannel')}</th>
                    <th className="px-4 py-2 text-right">
                      {t('acquisitionChannelsPage.colNewUsers')}
                    </th>
                    <th className="px-4 py-2 text-right">
                      {t('acquisitionChannelsPage.colConvertedUsers')}
                    </th>
                    <th className="px-4 py-2 text-right">
                      {t('acquisitionChannelsPage.colConversionRate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.channels ?? []).map((c: AcquisitionChannelRow) => (
                    <tr key={c.channel} className="border-b border-border">
                      <td className="px-4 py-3 text-foreground">
                        {channelLabel[c.channel] ?? c.channel}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-foreground">
                        {c.new_users}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-foreground">
                        {c.converted_users}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                        {c.conversion_rate === null
                          ? '—'
                          : `${(c.conversion_rate * 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                  {!data?.channels?.length && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        {t('acquisitionChannelsPage.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
